<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import {
  type CreateScanRequestData,
  type ScanChannel,
  type ScanChannelRequiredField,
  type ScanRequest,
  scanChannelsApi,
  scanRequestsApi,
} from '@/api/entities'
import { useClientStore } from '@/stores/client'

definePage({
  meta: {
    action: 'read',
    subject: 'Review',
  },
})

const { snackbar, handleError } = useErrorHandler()
const clientStore = useClientStore()

// 상태
const scanRequests = ref<ScanRequest[]>([])
const scanChannels = ref<ScanChannel[]>([])
const loading = ref(false)
const createDialogOpen = ref(false)
const query = ref('')
const page = ref(1)
const itemsPerPage = ref(15)
const totalItems = ref(0)
const selectedItems = ref<ScanRequest[]>([])
const formSubmitting = ref(false)

// 폼 데이터
const formData = reactive<CreateScanRequestData>({
  channel_id: 0,
  store_name: '',
  required_fields: {},
})

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', width: '80px' },
  { title: '스토어', key: 'store' },
  { title: '정보', key: 'required_fields' },
  { title: '상품 수', key: 'total_products', align: 'end' as const },
  { title: '리뷰 수', key: 'total_reviews', align: 'end' as const },
  { title: '상태', key: 'status', width: '120px' },
  { title: '등록일', key: 'created_at', width: '120px' },
  { title: '', key: 'actions', width: '60px', sortable: false },
]

// 선택된 채널의 required_fields
const selectedChannelRequiredFields = computed<ScanChannelRequiredField[]>(() => {
  if (!formData.channel_id)
    return []
  const channel = scanChannels.value.find(c => c.id === formData.channel_id)

  return channel?.required_fields || []
})

// 선택된 채널의 notice
const selectedChannelNotice = computed<string | null>(() => {
  if (!formData.channel_id)
    return null
  const channel = scanChannels.value.find(c => c.id === formData.channel_id)

  return channel?.notice || null
})

// 선택된 항목들의 합계
const selectedTotalProducts = computed(() =>
  selectedItems.value.reduce((sum, item) => sum + (item.total_products || 0), 0),
)

const selectedCollectableReviews = computed(() =>
  selectedItems.value.reduce((sum, item) => sum + (item.collectable_reviews || 0), 0),
)

// 숫자 포맷팅
const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null)
    return '0'

  return new Intl.NumberFormat('ko-KR').format(value)
}

// 날짜 포맷팅
const formatDate = (value: string): string => {
  return new Intl.DateTimeFormat('ko-KR').format(new Date(value))
}

// 스캔 요청 목록 로드
const loadScanRequests = async () => {
  loading.value = true
  try {
    const response = await scanRequestsApi.listScanRequests({
      page: page.value,
      per_page: itemsPerPage.value,
      query: query.value || undefined,
    })

    if (response.success && response.data) {
      const data = response.data as any

      scanRequests.value = data.data || data
      totalItems.value = data.meta?.total || scanRequests.value.length
    }
    else if (response.error) {
      handleError(response.error, { notificationMessage: '스캔 요청 목록을 불러오는데 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '스캔 요청 목록을 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 스캔 채널 목록 로드
const loadScanChannels = async () => {
  try {
    const response = await scanChannelsApi.listScanChannels()
    if (response.success && response.data) {
      const data = response.data as any

      scanChannels.value = data.data || data
    }
    else if (response.error) {
      handleError(response.error, { notificationMessage: '스캔 채널 목록을 불러오는데 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '스캔 채널 목록을 불러오는데 실패했습니다.' })
  }
}

// 디바운스된 검색
const debouncedSearch = useDebounceFn(() => {
  page.value = 1
  loadScanRequests()
}, 500)

// 검색어 변경 감시
watch(query, () => {
  debouncedSearch()
})

// 채널 변경 시 required_fields 초기화
watch(() => formData.channel_id, () => {
  formData.required_fields = {}
})

// 폼 데이터 초기화
const resetFormData = () => {
  formData.channel_id = 0
  formData.store_name = ''
  formData.required_fields = {}
}

// 폼 유효성 검사
const isFormValid = computed(() => {
  if (!formData.channel_id || !formData.store_name.trim())
    return false

  // 모든 required_fields가 입력되었는지 확인
  for (const field of selectedChannelRequiredFields.value) {
    if (!formData.required_fields[field.key]?.trim())
      return false
  }

  return true
})

// 스캔 요청 생성
const createScanRequest = async () => {
  if (!isFormValid.value)
    return

  formSubmitting.value = true
  try {
    const response = await scanRequestsApi.createScanRequest(formData)
    if (response.success) {
      snackbar.success('스캔 요청이 등록되었습니다.')
      createDialogOpen.value = false
      resetFormData()
      loadScanRequests()
    }
    else if (response.error) {
      handleError(response.error, { notificationMessage: '스캔 요청 생성에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '스캔 요청 생성에 실패했습니다.' })
  }
  finally {
    formSubmitting.value = false
  }
}

// 스캔 요청 삭제
const deleteScanRequest = async (id: number | string) => {
  if (!confirm('정말로 이 스캔 요청을 삭제하시겠습니까?'))
    return

  try {
    const response = await scanRequestsApi.deleteScanRequest(id)
    if (response.success) {
      snackbar.success('스캔 요청이 삭제되었습니다.')
      loadScanRequests()
    }
    else if (response.error) {
      handleError(response.error, { notificationMessage: '스캔 요청 삭제에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '스캔 요청 삭제에 실패했습니다.' })
  }
}

// 상태 배지 색상
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'secondary',
    processing: 'info',
    completed: 'success',
    failed: 'error',
  }

  return colors[status] || 'default'
}

// 상태 라벨
const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    pending: '대기 중',
    processing: '스캔 중',
    completed: '스캔 완료',
    failed: '스캔 실패',
  }

  return labels[status] || status
}

// 선택 초기화
const clearSelection = () => {
  selectedItems.value = []
}

// 힌트 텍스트 하이라이트 처리
const getHighlightedHint = (field: ScanChannelRequiredField): string => {
  if (!field.hint)
    return ''
  const before = field.hint.slice(0, field.hint_highlight_from)
  const highlight = field.hint.slice(field.hint_highlight_from, field.hint_highlight_to)
  const after = field.hint.slice(field.hint_highlight_to)

  return `${before}<span class="bg-success text-white px-1">${highlight}</span>${after}`
}

// 초기화
onMounted(() => {
  loadScanRequests()
  loadScanChannels()
})
</script>

<template>
  <VRow>
    <!-- 메인 테이블 -->
    <VCol
      cols="12"
      :lg="selectedItems.length > 0 ? 9 : 12"
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between pa-6">
          <div>
            <h4 class="text-h4">
              리뷰 스캔
            </h4>
            <p
              v-if="clientStore.selectedClient"
              class="text-body-2 mb-0 mt-1"
            >
              현재 선택된 고객사: <strong>{{ clientStore.selectedClient.name }}</strong>
            </p>
          </div>
          <div class="d-flex gap-2">
            <VTextField
              v-model="query"
              placeholder="업체명 검색"
              density="compact"
              hide-details
              style="width: 200px"
              clearable
            />
            <VBtn
              icon
              variant="text"
              @click="loadScanRequests"
            >
              <VIcon icon="tabler-refresh" />
            </VBtn>
            <VBtn
              color="primary"
              @click="createDialogOpen = true"
            >
              <VIcon
                start
                icon="tabler-plus"
              />
              스캔 추가
            </VBtn>
          </div>
        </VCardTitle>

        <VDivider />

        <VCardText>
          <VDataTable
            v-model="selectedItems"
            :headers="headers"
            :items="scanRequests"
            :loading="loading"
            :items-per-page="itemsPerPage"
            show-select
            item-value="id"
            return-object
            class="scan-table"
          >
            <template #item.store="{ item }">
              <div>
                {{ item.store_name }}
                <br>
                <small class="text-disabled">({{ item.channel?.name || item.channel }})</small>
              </div>
            </template>

            <template #item.required_fields="{ item }">
              <div v-if="item.required_fields">
                <small
                  v-for="(value, key) in item.required_fields"
                  :key="key"
                  class="d-block"
                >
                  {{ key }}: {{ value }}
                </small>
              </div>
            </template>

            <template #item.total_products="{ item }">
              {{ formatNumber(item.total_products) }}
            </template>

            <template #item.total_reviews="{ item }">
              <div class="text-end">
                {{ formatNumber(item.total_reviews) }}
                <br>
                <small class="text-disabled">({{ formatNumber(item.collectable_reviews) }})</small>
              </div>
            </template>

            <template #header.total_reviews>
              <div class="text-end">
                리뷰 수
                <br>
                <small>(수집 가능 수)</small>
              </div>
            </template>

            <template #item.status="{ item }">
              <VChip
                :color="getStatusColor(item.status)"
                size="small"
                variant="tonal"
              >
                {{ getStatusLabel(item.status) }}
              </VChip>
            </template>

            <template #item.created_at="{ item }">
              <span :title="item.created_at">{{ formatDate(item.created_at) }}</span>
            </template>

            <template #item.actions="{ item }">
              <VBtn
                icon
                size="small"
                variant="text"
                color="error"
                @click="deleteScanRequest(item.id)"
              >
                <VIcon icon="tabler-x" />
              </VBtn>
            </template>

            <template #bottom>
              <div class="d-flex align-center justify-space-between pa-4">
                <span>총 {{ formatNumber(totalItems) }}개</span>
                <VPagination
                  v-model="page"
                  :length="Math.ceil(totalItems / itemsPerPage)"
                  :total-visible="5"
                  @update:model-value="loadScanRequests"
                />
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 선택된 항목 사이드 패널 -->
    <VCol
      v-if="selectedItems.length > 0"
      cols="12"
      lg="3"
    >
      <VCard>
        <VCardTitle class="pa-4">
          선택된 스캔 결과
        </VCardTitle>
        <VDivider />
        <VCardText class="pa-0">
          <VTable density="compact">
            <thead>
              <tr>
                <th class="text-start">
                  스토어
                </th>
                <th class="text-end">
                  상품 수
                </th>
                <th class="text-end">
                  수집 가능 리뷰 수
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in selectedItems"
                :key="item.id"
              >
                <td>
                  <small>{{ item.store_name }}</small>
                  <br>
                  <small class="text-disabled">({{ item.channel?.name || item.channel }})</small>
                </td>
                <td class="text-end">
                  {{ formatNumber(item.total_products) }}
                </td>
                <td class="text-end">
                  {{ formatNumber(item.collectable_reviews) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-weight-bold">
                <td>총 {{ formatNumber(selectedItems.length) }}개 채널</td>
                <td class="text-end">
                  {{ formatNumber(selectedTotalProducts) }}
                </td>
                <td class="text-end">
                  {{ formatNumber(selectedCollectableReviews) }}
                </td>
              </tr>
            </tfoot>
          </VTable>
        </VCardText>
        <VCardActions class="pa-4">
          <VBtn
            block
            variant="outlined"
            color="primary"
            @click="clearSelection"
          >
            초기화
          </VBtn>
        </VCardActions>
      </VCard>
    </VCol>
  </VRow>

  <!-- 새 스캔 요청 다이얼로그 -->
  <VDialog
    v-model="createDialogOpen"
    max-width="500"
    persistent
    @after-leave="resetFormData"
  >
    <VCard>
      <VCardTitle class="pa-6">
        스캔 추가
      </VCardTitle>
      <VDivider />
      <VCardText class="pa-6">
        <!-- 채널 알림 -->
        <VAlert
          v-if="selectedChannelNotice"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          <div
            style="white-space: pre-line"
            v-html="selectedChannelNotice"
          />
        </VAlert>

        <!-- 채널 선택 -->
        <VSelect
          v-model="formData.channel_id"
          :items="scanChannels"
          item-title="name"
          item-value="id"
          label="채널 선택"
          :item-props="(item: ScanChannel) => ({
            disabled: !item.active,
            subtitle: !item.active ? '(점검중)' : undefined,
          })"
          class="mb-4"
        />

        <!-- 스토어명 -->
        <VTextField
          v-model="formData.store_name"
          label="스토어명"
          class="mb-4"
        />

        <!-- 동적 필드 -->
        <div
          v-for="field in selectedChannelRequiredFields"
          :key="field.key"
          class="mb-4"
        >
          <VTextField
            v-model="formData.required_fields[field.key]"
            :label="field.name"
          />
          <small
            v-if="field.hint"
            class="text-medium-emphasis"
            v-html="getHighlightedHint(field)"
          />
        </div>
      </VCardText>
      <VCardActions class="pa-6 pt-0">
        <VSpacer />
        <VBtn
          variant="text"
          :disabled="formSubmitting"
          @click="createDialogOpen = false"
        >
          취소
        </VBtn>
        <VBtn
          color="primary"
          :disabled="!isFormValid || formSubmitting"
          :loading="formSubmitting"
          @click="createScanRequest"
        >
          추가
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- 스낵바 -->
  <VSnackbar
    v-model="snackbar.isVisible.value"
    :color="snackbar.color.value"
    :timeout="snackbar.timeout.value"
  >
    {{ snackbar.message.value }}
  </VSnackbar>
</template>

<style lang="scss">
.scan-table {
  .v-data-table__td {
    vertical-align: middle;
  }
}
</style>
